// Shared destination catalog: fixed list of tourism categories.
export const DESTINATION_CATEGORIES = [
  "praia",
  "serra",
  "ecoturismo",
  "aventura",
  "gastronomico",
  "cultural",
  "historico",
  "romantico",
  "familia",
  "urbano",
  "religioso",
  "rural",
] as const;

export type DestinationCategory = (typeof DESTINATION_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<
  DestinationCategory,
  { es: string; en: string; "pt-BR": string }
> = {
  praia: { es: "Playa", en: "Beach", "pt-BR": "Praia" },
  serra: { es: "Sierra", en: "Mountain", "pt-BR": "Serra" },
  ecoturismo: { es: "Ecoturismo", en: "Ecotourism", "pt-BR": "Ecoturismo" },
  aventura: { es: "Aventura", en: "Adventure", "pt-BR": "Aventura" },
  gastronomico: { es: "Gastronómico", en: "Culinary", "pt-BR": "Gastronômico" },
  cultural: { es: "Cultural", en: "Cultural", "pt-BR": "Cultural" },
  historico: { es: "Histórico", en: "Historic", "pt-BR": "Histórico" },
  romantico: { es: "Romántico", en: "Romantic", "pt-BR": "Romântico" },
  familia: { es: "Familiar", en: "Family", "pt-BR": "Família" },
  urbano: { es: "Urbano", en: "Urban", "pt-BR": "Urbano" },
  religioso: { es: "Religioso", en: "Religious", "pt-BR": "Religioso" },
  rural: { es: "Rural", en: "Rural", "pt-BR": "Rural" },
};

export interface DestinationRelevantInfo {
  pet_friendly?: string;
  lgbt_friendly?: string;
  atms?: string;
  gas_stations?: string;
  credit_card?: string;
  recommended_days?: string;
  best_season?: string;
  cost_meals?: string;
  cost_transport?: string;
  cost_activities?: string;
}

export interface DestinationLocaleContent {
  slogan: string;
  meta_description: string;
  intro: string;
  known_for: string[];
  getting_around: string;
  secret_tips: string;
  relevant_info: DestinationRelevantInfo;
}

export interface DestinationRecord {
  id: string;
  slug: string;
  iterpec_destination_id: string | null;
  name: string;
  state: string | null;
  country: string | null;
  slogan_es: string | null;
  slogan_en: string | null;
  slogan_pt: string | null;
  meta_description_es: string | null;
  meta_description_en: string | null;
  meta_description_pt: string | null;
  intro_es: string | null;
  intro_en: string | null;
  intro_pt: string | null;
  known_for_es: string[];
  known_for_en: string[];
  known_for_pt: string[];
  getting_around_es: string | null;
  getting_around_en: string | null;
  getting_around_pt: string | null;
  secret_tips_es: string | null;
  secret_tips_en: string | null;
  secret_tips_pt: string | null;
  relevant_info: Record<string, { es?: string; en?: string; pt?: string }>;
  latitude: number | null;
  longitude: number | null;
  google_place_id: string | null;
  hero_image_url: string | null;
  hero_image_credit: string | null;
  hero_image_source: string | null;
  gallery_images: string[];
  categories: string[];
  status: "draft" | "published";
  visit_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DestinationHotelSummary {
  hotel_id: string;
  slug: string;
  name: string;
  star_rating: number | null;
  city: string | null;
  country: string | null;
  thumb_url: string | null;
  latitude: number | null;
  longitude: number | null;
  featured: boolean;
  visit_count: number;
}

/** Locale-agnostic getter — reads the right column set for the active locale. */
export function pickLocaleContent(
  d: DestinationRecord,
  locale: "es" | "en" | "pt-BR" | "en-EU",
): DestinationLocaleContent {
  const key: "es" | "en" | "pt" =
    locale === "pt-BR" ? "pt" : locale === "es" ? "es" : "en";
  const relevant: DestinationRelevantInfo = {};
  for (const [k, v] of Object.entries(d.relevant_info ?? {})) {
    const val =
      key === "pt" ? v?.pt : key === "es" ? v?.es : v?.en;
    if (val) (relevant as Record<string, string>)[k] = val;
  }
  return {
    slogan: (d as any)[`slogan_${key}`] ?? "",
    meta_description: (d as any)[`meta_description_${key}`] ?? "",
    intro: (d as any)[`intro_${key}`] ?? "",
    known_for: ((d as any)[`known_for_${key}`] ?? []) as string[],
    getting_around: (d as any)[`getting_around_${key}`] ?? "",
    secret_tips: (d as any)[`secret_tips_${key}`] ?? "",
    relevant_info: relevant,
  };
}

export const RELEVANT_INFO_KEYS: Array<keyof DestinationRelevantInfo> = [
  "pet_friendly",
  "lgbt_friendly",
  "atms",
  "gas_stations",
  "credit_card",
  "recommended_days",
  "best_season",
  "cost_meals",
  "cost_transport",
  "cost_activities",
];

export const RELEVANT_INFO_LABELS: Record<
  keyof DestinationRelevantInfo,
  { es: string; en: string; "pt-BR": string }
> = {
  pet_friendly: { es: "Pet friendly", en: "Pet friendly", "pt-BR": "Pet friendly" },
  lgbt_friendly: { es: "LGBT friendly", en: "LGBT friendly", "pt-BR": "LGBT friendly" },
  atms: { es: "Cajeros automáticos", en: "ATMs", "pt-BR": "Caixas eletrônicos" },
  gas_stations: { es: "Estaciones de servicio", en: "Gas stations", "pt-BR": "Postos de gasolina" },
  credit_card: { es: "Aceptación de tarjetas", en: "Card acceptance", "pt-BR": "Aceitação de cartão" },
  recommended_days: { es: "Días recomendados", en: "Recommended days", "pt-BR": "Dias recomendados" },
  best_season: { es: "Mejor época", en: "Best season", "pt-BR": "Melhor época" },
  cost_meals: { es: "Gasto en comidas", en: "Meals cost", "pt-BR": "Gasto com refeições" },
  cost_transport: { es: "Gasto en transporte", en: "Transport cost", "pt-BR": "Gasto com transporte" },
  cost_activities: { es: "Gasto en actividades", en: "Activities cost", "pt-BR": "Gasto com atividades" },
};
